import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import PageLayout from '../../components/layout/PageLayout';
import { useCreateCallRecordMutation, useLazyGetLeadsQuery } from '../../store/api';
import { extractList } from '../../utils/response';
import { getErrorMessage } from '../../utils/errorHandler';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { CALL_RECORDS_LIST_PATH } from '../../navigations/path';
import { buildLeadsAutocompleteParams } from '../../components/Leads/utils';
import { normalizeLeadOption, loadCallRecordStatuses } from './callRecordFormMeta';
import { useLazySelectOptions } from '../../hooks/useLazySelectOptions';
import { useServerValidationErrors } from '../../hooks/useServerValidationErrors';
import CallRecordingUploadSection from './CallRecordingUploadSection';
import {
  uploadRecordingAttachmentFileChunked,
  extractAttachmentIdFromUploadResponse,
} from './callRecordAttachmentUpload';
import { isAudioFile } from './recordingFileTypes';


const makeRowKey = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `r-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

const CreateCallRecord = () => {
  const navigate = useNavigate();
  const [createCallRecord, { isLoading: submitting }] = useCreateCallRecordMutation();
  const [fetchLeadsTrigger] = useLazyGetLeadsQuery();
  const { errors, setErrors, applyApiError, clearError, fieldProps } =
    useServerValidationErrors({ recordings_0: 'recordings' });

  const callRecordStatusTrigger = useCallback(
    () => ({
      unwrap: () =>
        loadCallRecordStatuses().then((list) => {
          if (!list.length) {
            toast.warn(
              'No statuses found for recordings. Check model name on /statuses (CallRecord) or permissions.'
            );
          }
          return list;
        }),
    }),
    []
  );

  const statusSelect = useLazySelectOptions(callRecordStatusTrigger, {
    mapResult: (res) => (Array.isArray(res) ? res : []),
  });
  const statusOptions = statusSelect.options;

  const [leadInput, setLeadInput] = useState('');
  const [leadOptions, setLeadOptions] = useState([]);
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadsAutocompleteOpen, setLeadsAutocompleteOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [statusId, setStatusId] = useState('');
  const [note, setNote] = useState('');
  /** Each row: local key, file, attachment id after upload, uploading flag */
  const [recordingItems, setRecordingItems] = useState([]);

  const prevLeadIdRef = useRef(undefined);

  useEffect(() => {
    const id = selectedLead?.id ?? selectedLead?.uuid;
    if (prevLeadIdRef.current !== undefined && id !== prevLeadIdRef.current) {
      setRecordingItems([]);
    }
    prevLeadIdRef.current = id;
  }, [selectedLead?.id, selectedLead?.uuid]);

  const fetchLeads = useCallback(
    async (searchTerm) => {
      setLeadLoading(true);
      try {
        const params = buildLeadsAutocompleteParams({
          search: searchTerm,
        });
        const res = await fetchLeadsTrigger(params).unwrap();
        const raw = extractList(res);
        const list = (Array.isArray(raw) ? raw : []).map(normalizeLeadOption).filter(Boolean);
        setLeadOptions(list);
      } catch (err) {
        toast.error(getErrorMessage(err) || 'Failed to load leads');
        setLeadOptions([]);
      } finally {
        setLeadLoading(false);
      }
    },
    [fetchLeadsTrigger]
  );

  useEffect(() => {
    if (!leadsAutocompleteOpen) return;
    const t = setTimeout(() => fetchLeads(leadInput), 350);
    return () => clearTimeout(t);
  }, [leadInput, fetchLeads, leadsAutocompleteOpen]);

  const handleLeadsAutocompleteOpen = () => {
    setLeadsAutocompleteOpen(true);
    void fetchLeads(leadInput);
  };

  const handleStatusSelectOpen = () => {
    statusSelect.load().catch((err) => {
      toast.error(err?.message || 'Failed to load statuses');
    });
  };

  const handleRecordingFiles = async (files) => {
    const list = Array.isArray(files) ? files : Array.from(files || []);
    const audioFiles = list.filter(isAudioFile);
    if (!audioFiles.length) {
      toast.warn('Please choose audio files (e.g. MP3, WAV, M4A).');
      return;
    }
    const leadKey = selectedLead?.id ?? selectedLead?.uuid;
    if (!leadKey) {
      toast.warn('Select a lead before uploading recordings.');
      return;
    }

    let uploaded = 0;
    for (const file of audioFiles) {
      const key = makeRowKey();
      setRecordingItems((prev) => [...prev, { key, file, attachmentId: null, uploading: true }]);
      try {
        const uploadBody = await uploadRecordingAttachmentFileChunked(file, { modelId: leadKey });
        const attachmentId = extractAttachmentIdFromUploadResponse(uploadBody);
        if (!attachmentId) {
          toast.error(`Upload finished but no attachment id for "${file.name}".`);
          setRecordingItems((prev) => prev.filter((r) => r.key !== key));
          continue;
        }
        setRecordingItems((prev) =>
          prev.map((r) =>
            r.key === key ? { ...r, attachmentId: String(attachmentId), uploading: false } : r
          )
        );
        uploaded += 1;
      } catch (err) {
        toast.error(
          getErrorMessage(err) || err.response?.data?.message || `Upload failed: ${file.name}`
        );
        setRecordingItems((prev) => prev.filter((r) => r.key !== key));
      }
    }
    if (uploaded > 0) {
      toast.success(uploaded === 1 ? 'Recording uploaded.' : `${uploaded} recordings uploaded.`);
    }
  };

  const handleRemoveRecordingRow = (key) => {
    setRecordingItems((prev) => prev.filter((r) => r.key !== key));
  };

  const attachmentIdsReady = recordingItems.filter((r) => r.attachmentId).map((r) => r.attachmentId);
  const anyUploading = recordingItems.some((r) => r.uploading);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const leadKey = selectedLead?.id ?? selectedLead?.uuid;

    const clientErrors = {};
    if (!leadKey) clientErrors.lead_id = 'Select a lead';
    if (!statusId) clientErrors.status_id = 'Select a status';
    if (!attachmentIdsReady.length)
      clientErrors.recordings = 'Upload at least one recording and wait for attachment ids.';
    setErrors(clientErrors);
    if (Object.keys(clientErrors).length > 0) return;
    if (anyUploading) {
      return;
    }

    try {
      const fd = new FormData();
      fd.append('lead_id', String(leadKey));
      fd.append('status_id', String(statusId));
      fd.append('note', note || '');
      attachmentIdsReady.forEach((id, i) => {
        fd.append(`recordings[${i}]`, String(id));
      });

      await createCallRecord(fd).unwrap();
      toast.success('Recording saved.');
      navigate(CALL_RECORDS_LIST_PATH);
    } catch (err) {
      applyApiError(err, 'Failed to save recording');
    }
  };

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
          Upload recording
        </Typography>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  options={leadOptions}
                  loading={leadLoading}
                  filterOptions={(x) => x}
                  getOptionLabel={(opt) => opt?.label || opt?.name || opt?.email || ''}
                  isOptionEqualToValue={(a, b) => (a?.id ?? a?.uuid) === (b?.id ?? b?.uuid)}
                  value={selectedLead}
                  onChange={(_, v) => {
                    setSelectedLead(v);
                    clearError('lead_id');
                  }}
                  onInputChange={(_, v) => setLeadInput(v)}
                  onOpen={handleLeadsAutocompleteOpen}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Lead"
                      placeholder="Search leads by name, email…"
                      required
                      {...fieldProps('lead_id')}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" required error={!!errors.status_id}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={statusId}
                    onChange={(e) => {
                      setStatusId(e.target.value);
                      clearError('status_id');
                    }}
                    onOpen={handleStatusSelectOpen}
                    disabled={statusSelect.loading && !statusOptions.length}
                  >
                    {statusOptions.map((s) => {
                      const val = s.id ?? s.uuid;
                      return (
                        <MenuItem key={String(val)} value={String(val)}>
                          {s.name ?? s.title ?? s.label ?? String(val)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {errors.status_id && (
                    <FormHelperText>{errors.status_id}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Note"
                  multiline
                  rows={3}
                  value={note}
                  onChange={(e) => {
                    setNote(e.target.value);
                    clearError('note');
                  }}
                  placeholder="Recording note…"
                  {...fieldProps('note')}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {errors.recordings && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {errors.recordings}
          </Typography>
        )}

        <CallRecordingUploadSection
          multiple
          items={recordingItems}
          onFilesSelected={(list) => {
            void handleRecordingFiles(list);
          }}
          onRemoveItem={handleRemoveRecordingRow}
          uploadingAttachment={anyUploading}
          saving={submitting}
          disabled={submitting}
        />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
          <Button
            type="button"
            variant="contained"
            disabled={
              submitting ||
              anyUploading ||
              !attachmentIdsReady.length
            }
            onClick={handleSubmit}
          >
            {submitting ? <CircularProgress size={22} /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" onClick={() => navigate(CALL_RECORDS_LIST_PATH)}>
            Cancel
          </Button>
        </Box>
      </Container>
    </PageLayout>
  );
};

export default CreateCallRecord;
